import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, Theme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { fuelMonitoringTheme, alternativeThemes } from './index';

type ThemeName = 'default' | 'blueGrey' | 'purpleDeep' | 'darkMode';

interface ThemeContextType {
  currentTheme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  availableThemes: { name: ThemeName; label: string; theme: Theme }[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const FuelMonitoringThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<ThemeName>('default');

  const availableThemes = [
    { name: 'default' as ThemeName, label: 'Blue & Green (Default)', theme: fuelMonitoringTheme },
    { name: 'blueGrey' as ThemeName, label: 'Blue & Grey', theme: alternativeThemes.blueGrey },
    { name: 'purpleDeep' as ThemeName, label: 'Purple & Teal', theme: alternativeThemes.purpleDeep },
    { name: 'darkMode' as ThemeName, label: 'Dark Mode', theme: alternativeThemes.darkMode },
  ];

  // Load saved theme from localStorage on component mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('fuelMonitoringTheme') as ThemeName;
    if (savedTheme && availableThemes.find(t => t.name === savedTheme)) {
      setCurrentTheme(savedTheme);
    }
  }, []);

  const setTheme = (theme: ThemeName) => {
    setCurrentTheme(theme);
    localStorage.setItem('fuelMonitoringTheme', theme);
  };

  const activeTheme = availableThemes.find(t => t.name === currentTheme)?.theme || fuelMonitoringTheme;

  return (
    <ThemeContext.Provider
      value={{
        currentTheme,
        setTheme,
        availableThemes,
      }}
    >
      <MuiThemeProvider theme={activeTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a FuelMonitoringThemeProvider');
  }
  return context;
};

export default FuelMonitoringThemeProvider;